import { api, ApiError, apiErrorFromResponse } from '../../lib/api';
import { firebaseAuth } from '../../lib/firebase';
import { hostedReportDownloadFilename } from '../../lib/hostedReport';
import type { HostedReportSummary } from '../../types/hostedReport';
import { uploadResumable } from '../portalAssets';
import type { BundleProblem } from '../../lib/hostedBundle';

// Tools → HTML Hosting — the authed management API.

export async function listHostedReports(): Promise<HostedReportSummary[]> {
  const res = await api<{ reports: HostedReportSummary[] }>('/api/hosted-reports');
  return res.reports;
}

export type CreateHostedReportArgs = {
  title: string;
  accountId: string | null;
  /** null = the link never expires. Required, so omission can't mean "never". */
  expiresAtMs: number | null;
  password: string | null;
  filename: string;
  html: string;
};

export async function createHostedReport(
  args: CreateHostedReportArgs,
): Promise<HostedReportSummary> {
  const res = await api<{ report: HostedReportSummary }>('/api/hosted-reports', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  return res.report;
}

export async function revokeHostedReport(id: string): Promise<HostedReportSummary> {
  const res = await api<{ report: HostedReportSummary }>(
    `/api/hosted-reports/${id}/revoke`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return res.report;
}

/**
 * The password's three states, named explicitly. `keep` is what the dialog
 * sends when the AM did not touch the control — an optional nullable string
 * would put "leave it alone" and "remove it" one keystroke apart.
 */
export type PasswordChange =
  | { action: 'keep' }
  | { action: 'set'; value: string }
  | { action: 'clear' };

export type ExpiryChange = { action: 'set'; atMs: number } | { action: 'never' };

export type UpdateHostedReportArgs = {
  title?: string;
  accountId?: string | null;
  /** Named, like the password: absent = keep, never = remove the expiry. */
  expiry?: ExpiryChange;
  password?: PasswordChange;
  file?: { filename: string; html: string };
};

/**
 * The ONE edit path. Absent fields are left alone, so the dialog sends only
 * what actually changed. There is deliberately no separate extend-expiry call.
 */
export async function updateHostedReport(
  id: string,
  args: UpdateHostedReportArgs,
): Promise<HostedReportSummary> {
  const res = await api<{ report: HostedReportSummary }>(`/api/hosted-reports/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
  return res.report;
}

export async function deleteHostedReport(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/hosted-reports/${id}`, { method: 'DELETE' });
}

/**
 * Pull the hosted file back down as a blob.
 *
 * ⚠️ IT CANNOT BE A PLAIN `<a href>`. The endpoint is behind `requireAuth`, and
 * an anchor navigation carries no Firebase ID token — the link would simply
 * 401, which reads as the feature being broken. So the request is a fetch with
 * the auth header, and the caller turns the blob into a save.
 *
 * ⚠️ Self-contained (own fetch + token) rather than going through `api()`,
 * which parses every response as JSON — the publishDraft precedent. What comes
 * back here is the report's bytes.
 *
 * The FILENAME is computed here, from the shared sanitiser the server uses to
 * build the `Content-Disposition` header, so the name the browser saves cannot
 * disagree with the name the server said. Parsing it back out of the header
 * would be a second implementation of the same answer.
 */
export async function downloadHostedReport(
  report: Pick<HostedReportSummary, 'id' | 'title'>,
): Promise<{ blob: Blob; filename: string }> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new ApiError(401, 'Not signed in');
  const token = await user.getIdToken();

  const res = await fetch(`/api/hosted-reports/${report.id}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    // The error body IS json (every AppError goes through the shared handler),
    // even though the success body is not.
    throw await apiErrorFromResponse(res);
  }

  return {
    blob: await res.blob(),
    filename: hostedReportDownloadFilename(report.title, report.id),
  };
}

/**
 * Hand a blob to the browser as a file save, then release it.
 *
 * ⚠️ THE REVOKE IS NOT HOUSEKEEPING. An object URL pins the WHOLE blob in
 * memory for the lifetime of the document, and one report on the current list
 * is 9.4 MB — a few downloads in a long-lived tab is real memory held for
 * nothing.
 */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- multi-file bundles (a .zip of a page and its folders) ----------
//
// ⚠️ THE ZIP NEVER GOES THROUGH OUR SERVER. `startBundleUpload` returns a signed
// resumable session URL and the browser uploads straight to storage (Cloud Run
// caps a request at 32 MiB; a bundle can be 200 MB). `finaliseBundle` then asks
// the server to unpack it, and is called AGAIN while it answers `processing` —
// the server works to a time budget so no request runs into the ~100s proxy limit.

export type StartBundleArgs = {
  title: string;
  accountId: string | null;
  expiresAtMs: number | null;
  password: string | null;
  filename: string;
  sizeBytes: number;
};

export async function startBundleUpload(
  args: StartBundleArgs,
): Promise<{ report: HostedReportSummary; upload: { uploadUrl: string; requiredHeaders: Record<string, string> } }> {
  return api('/api/hosted-reports/bundle/start', { method: 'POST', body: JSON.stringify(args) });
}

export type FinaliseResult =
  | { status: 'ready'; report: HostedReportSummary }
  | { status: 'processing'; written: number; total: number }
  | { status: 'needs_entry'; candidates: string[] };

/** The archive was refused. `problems` names each file and why. */
export class BundleRejectedError extends Error {
  constructor(message: string, readonly problems: BundleProblem[]) {
    super(message);
    this.name = 'BundleRejectedError';
  }
}

export async function finaliseBundle(id: string, entryPath?: string): Promise<FinaliseResult> {
  try {
    return await api<FinaliseResult>(`/api/hosted-reports/${id}/bundle/finalise`, {
      method: 'POST',
      body: JSON.stringify(entryPath ? { entryPath } : {}),
    });
  } catch (err) {
    // The 422 body carries the per-file reasons; ApiError keeps the message only.
    if (err instanceof ApiError && err.status === 422) {
      throw new BundleRejectedError(err.message, []);
    }
    throw err;
  }
}

export type BundleUploadPhase =
  | { phase: 'uploading'; sentBytes: number; totalBytes: number }
  | { phase: 'unpacking'; written: number; total: number }
  | { phase: 'needs_entry'; candidates: string[]; reportId: string };

/**
 * Upload → unpack → ready, reporting progress. Resolves with the ready report, or
 * with `needs_entry` when staff must pick among several root pages (then call
 * `finaliseBundle(id, chosen)` and keep looping with `unpackUntilReady`).
 */
export async function uploadBundle(
  args: StartBundleArgs & { file: Blob },
  onPhase: (p: BundleUploadPhase) => void,
): Promise<{ report: HostedReportSummary } | { needsEntry: { reportId: string; candidates: string[] } }> {
  const { file, ...meta } = args;
  const { report, upload } = await startBundleUpload(meta);
  await uploadResumable({
    file,
    uploadUrl: upload.uploadUrl,
    requiredHeaders: upload.requiredHeaders,
    onProgress: (sentBytes, totalBytes) => onPhase({ phase: 'uploading', sentBytes, totalBytes }),
  });
  return unpackUntilReady(report.id, undefined, onPhase);
}

export async function unpackUntilReady(
  reportId: string,
  entryPath: string | undefined,
  onPhase: (p: BundleUploadPhase) => void,
  maxRounds = 30,
): Promise<{ report: HostedReportSummary } | { needsEntry: { reportId: string; candidates: string[] } }> {
  for (let i = 0; i < maxRounds; i += 1) {
    let res: FinaliseResult;
    try {
      res = await finaliseBundle(reportId, entryPath);
    } catch (err) {
      // 409 = another finalise holds the lease: wait and look again.
      if (err instanceof ApiError && err.status === 409) {
        await new Promise((r) => setTimeout(r, 3000));
        continue;
      }
      throw err;
    }
    if (res.status === 'ready') return { report: res.report };
    if (res.status === 'needs_entry') {
      onPhase({ phase: 'needs_entry', candidates: res.candidates, reportId });
      return { needsEntry: { reportId, candidates: res.candidates } };
    }
    onPhase({ phase: 'unpacking', written: res.written, total: res.total });
  }
  throw new Error('Unpacking is taking unusually long. Reload the page to see whether it finished.');
}
